import { Input } from '@aigc/antd';
import { VideoThumbnail } from '@aigc/components';
import { useAccept, useAutoPaste } from '@aigc/hooks';
import { useOverlay } from '@aigc/overlay-hook';
import type { Pagination } from '@aigc/types';
import {
  PictureOutlined,
  VideoCameraOutlined,
  VideoCameraTwoTone,
} from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Alert, App, Button, Form, Table, Upload } from 'antd';
import { RESET } from 'jotai/utils';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormControl } from '../../../components/form-control';
import { Permission } from '../../../features/common/permision';
import {
  createBeforeUpload,
  createUploadValidator,
} from '../../../features/file';
import { usePreviewFn } from '../../../features/file/hooks';
import {
  getBaseTaskListQueryKey,
  getTaskListQueryOptions,
} from '../../../features/task/api';
import { TaskStatus } from '../../../features/task/components/task-status';
import {
  TaskStatusCode,
  TaskType,
  type VideoReplicationTaskDto,
} from '../../../features/task/model';
import { replicateVideo } from '../../../features/video-replication/api';
import {
  VideoReplicationDrawer,
  type VideoReplicationDrawerCloseReason,
} from '../../../features/video-replication/components/video-replication-drawer';
import {
  buildVideoReplicationInput,
  videoReplicationFormSchema,
} from '../../../features/video-replication/schema';
import { useVideoReplicationCurrentTaskId } from '../../../features/video-replication/store';
import { formatVideoReplicationTaskElapsed } from '../../../features/video-replication/utils';

function VideoReplicationPage() {
  const { message, notification } = App.useApp();
  const on_preview = usePreviewFn();
  const query_client = useQueryClient();
  const [openOverlay, closeOverlay] =
    useOverlay<VideoReplicationDrawerCloseReason>();
  const [currentTaskId, setCurrentTaskId] = useVideoReplicationCurrentTaskId();
  const activeTaskIdRef = useRef<string | undefined>(undefined);
  const [pagination, set_pagination] = useState<Pagination>({
    page: 1,
    size: 10,
  });
  const form = useForm({
    resolver: yupResolver(videoReplicationFormSchema),
    defaultValues: {
      video_file: [],
      reference_images: [],
      extra_prompt: null,
    },
  });
  const replication = useMutation(replicateVideo);
  const task_list_query = useQuery({
    ...getTaskListQueryOptions({
      ...pagination,
      task_types: [TaskType.视频复刻],
    }),
    placeholderData: keepPreviousData,
    refetchInterval: (query) => {
      const tasks = query.state.data?.data;

      if (!tasks) {
        return 5000;
      }

      return tasks.some((task) => !TaskStatus.isSettled(task.status))
        ? 5000
        : false;
    },
    refetchIntervalInBackground: true,
  });

  const openVideoReplicationDrawer = useCallback(
    (taskId: string) => {
      if (activeTaskIdRef.current === taskId) {
        return;
      }

      activeTaskIdRef.current = taskId;
      setCurrentTaskId(taskId);
      const overlayRef = openOverlay(
        <VideoReplicationDrawer
          taskId={taskId}
          onTaskIdChange={(nextTaskId) => {
            activeTaskIdRef.current = nextTaskId;
            setCurrentTaskId(nextTaskId);
          }}
        />,
      );

      overlayRef.afterClosed.then((reason) => {
        if (reason === 'navigate') {
          return;
        }

        activeTaskIdRef.current = undefined;

        if (reason === 'manual') {
          setCurrentTaskId(RESET);
        }
      });
    },
    [openOverlay, setCurrentTaskId],
  );

  useEffect(() => {
    if (currentTaskId) {
      openVideoReplicationDrawer(currentTaskId);
      return;
    }

    if (activeTaskIdRef.current) {
      activeTaskIdRef.current = undefined;
      closeOverlay();
    }
  }, [closeOverlay, openVideoReplicationDrawer, currentTaskId]);

  const handleStartReplication = form.handleSubmit(async (values) => {
    lfq('track', 'video.replication');

    const task = await replication.mutateAsync(
      buildVideoReplicationInput(values),
    );
    set_pagination((current) => ({ ...current, page: 1 }));
    await query_client.invalidateQueries({
      queryKey: getBaseTaskListQueryKey(),
    });
    message.success('视频复刻任务创建成功');
    openVideoReplicationDrawer(task.id);
  });

  const accept_video = useAccept(['video/*']);
  useAutoPaste('#replicationVideoUploader', {
    filter: accept_video.filter,
  });

  const accept_image = useAccept(['.jpg', '.jpeg', '.png', '.webp']);
  useAutoPaste('#referenceImageUploader', {
    filter: accept_image.filter,
  });

  return (
    <div className="flex h-full w-full flex-col divide-y divide-primary-200 overflow-y-auto sm:flex-row sm:divide-x sm:divide-y-0">
      <div className="card flex w-full flex-none flex-col sm:overflow-auto xl:w-[480px]">
        <div className="mb-6">
          <h2 className="text-xl font-bold text-primary-900">爆款复刻</h2>
          <p className="mt-2 text-sm text-primary-500">
            上传爆款视频和参考素材，生成可编辑的复刻脚本
          </p>
        </div>

        <Form layout="vertical" onFinish={handleStartReplication}>
          <FormControl
            control={form.control}
            name="video_file"
            label="爆款视频"
            required
            exact
            render={({ field }) => (
              <Upload.Dragger
                id="replicationVideoUploader"
                ref={field.ref}
                name={field.name}
                disabled={field.disabled}
                classNames={{ list: 'mt-2' }}
                listType="picture"
                fileList={field.value}
                iconRender={() => <VideoCameraTwoTone />}
                beforeUpload={createBeforeUpload({
                  validator: createUploadValidator({ accept: '.mp4' }),
                  onError: ({ title, message }) => {
                    notification.error({ title, description: message });
                  },
                })}
                maxCount={1}
                accept=".mp4"
                onPreview={on_preview}
                onChange={(info) => {
                  if (info.file.status === 'error') {
                    notification.error({
                      title: '错误',
                      description: `视频 ${info.file.name} 上传失败，请重试`,
                    });
                  }

                  field.onChange(info.fileList);
                }}
              >
                <p className="ant-upload-drag-icon">
                  <VideoCameraOutlined />
                </p>
                <p className="ant-upload-text">
                  点击、拖放或<span className="text-blue-500">粘贴</span>
                  上传视频
                </p>
              </Upload.Dragger>
            )}
          />

          <FormControl
            control={form.control}
            name="reference_images"
            label="想替换成的商品、人物、场景"
            exact
            render={({ field }) => (
              <Upload.Dragger
                id="referenceImageUploader"
                ref={field.ref}
                name={field.name}
                disabled={field.disabled}
                classNames={{ list: 'mt-2' }}
                listType="picture"
                fileList={field.value ?? []}
                multiple
                maxCount={9}
                accept=".jpg,.jpeg,.png,.webp"
                beforeUpload={createBeforeUpload({
                  validator: createUploadValidator({
                    accept: '.jpg,.jpeg,.png,.webp',
                  }),
                  onError: ({ title, message }) => {
                    notification.error({ title, description: message });
                  },
                })}
                onPreview={on_preview}
                onChange={(info) => {
                  if (info.file.status === 'error') {
                    notification.error({
                      title: '错误',
                      description: `图片 ${info.file.name} 上传失败，请重试`,
                    });
                  }

                  field.onChange(info.fileList);
                }}
              >
                <p className="ant-upload-drag-icon">
                  <PictureOutlined />
                </p>
                <p className="ant-upload-text">
                  点击、拖放或<span className="text-blue-500">粘贴</span>
                  上传图片
                </p>
                <p className="ant-upload-hint">
                  可以上传你想替换成的商品、人物、场景，可以上传多张图片
                </p>
              </Upload.Dragger>
            )}
          />

          <FormControl
            control={form.control}
            name="extra_prompt"
            label="其他要求"
            render={({ field }) => (
              <Input.TextArea
                {...field}
                value={field.value ?? ''}
                placeholder="比如强调卖点，使用人群，使用场景等你想要强调的内容"
                autoSize={{ minRows: 3, maxRows: 6 }}
              />
            )}
          />

          <Button
            htmlType="submit"
            variant="solid"
            color="primary"
            size="large"
            loading={replication.isPending}
            block
          >
            🚀 {replication.isPending ? '复刻中' : '开始复刻'}
          </Button>
        </Form>
      </div>

      <div className="card min-h-[360px] min-w-0 flex-none overflow-y-auto sm:flex-auto">
        <h2 className="mb-4 text-xl font-bold">历史复刻</h2>
        <Table<VideoReplicationTaskDto>
          size="middle"
          tableLayout="fixed"
          loading={task_list_query.isLoading}
          dataSource={task_list_query.data?.data as VideoReplicationTaskDto[]}
          rowKey={(row) => row.id}
          onRow={(row) => ({
            className: 'cursor-pointer',
            onClick: () => {
              openVideoReplicationDrawer(row.id);
            },
          })}
          columns={[
            {
              title: '爆款视频',
              width: 96,
              render: (_, task) => (
                <div onClick={(event) => event.stopPropagation()}>
                  <VideoThumbnail
                    size={64}
                    src={task.generate_data?.video.url}
                  />
                </div>
              ),
            },
            {
              title: '任务',
              width: 200,
              render: (_, task) => (
                <div className="min-w-0 space-y-2">
                  <div className="truncate font-medium">
                    {task.result?.product_name || '视频复刻'}
                  </div>
                  <span className="inline-flex rounded bg-primary-100 px-2 py-1 text-xs">
                    # {task.id}
                  </span>
                </div>
              ),
            },
            {
              title: '状态',
              render: (_, task) => (
                <div className="space-y-2">
                  <TaskStatus task={task} />
                  {task.status === TaskStatusCode.任务失败 && (
                    <Alert
                      className="break-words"
                      type="error"
                      title={task.error ?? '视频复刻失败'}
                    />
                  )}
                </div>
              ),
            },
            {
              title: '任务耗时',
              width: 140,
              render: (_, task) => formatVideoReplicationTaskElapsed(task),
            },
            {
              title: '创建时间',
              width: 180,
              render: (_, task) => new Date(task.created_at).toLocaleString(),
            },
          ]}
          pagination={{
            total: task_list_query.data?.total ?? 0,
            current: pagination.page,
            pageSize: pagination.size,
            simple: true,
            hideOnSinglePage: true,
            onChange: (page, size) => {
              set_pagination({ page, size });
            },
          }}
        />
      </div>
    </div>
  );
}

export const Route = createFileRoute('/_creator-center/video/replication')({
  staticData: { permissions: [Permission.视频复刻] },
  component: VideoReplicationPage,
});
